import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sportingService } from '../../services/sportingService';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import type { Player, PlayerPerformance } from '../../types/player';
import toast from 'react-hot-toast';
import {
  User,
  ArrowLeft,
  MapPin,
  Activity,
  DollarSign,
  TrendingUp,
  Shield,
  Target,
  Zap,
  Heart,
  XCircle,
  Trophy,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { translatePosition, translateFoot } from '../../utils/positionTranslations';
import { formatMarketValue } from '../../utils/functions';


const PlayerDetailPage: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [player, setPlayer] = useState<Player | null>(null);
  const [performances, setPerformances] = useState<PlayerPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPerformances, setLoadingPerformances] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllSeasons, setShowAllSeasons] = useState(false);
  const [hasFbrefData, setHasFbrefData] = useState<boolean>(false);
  const currentLang = i18n.language as 'es' | 'en';

  useEffect(() => {
    if (playerId) {
      loadPlayerDetails();
      loadPlayerPerformances();
    }
  }, [playerId]);

  const loadPlayerDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sportingService.getPlayerById(playerId!);
      setPlayer(response.jugador);
      // Check if the response includes fbref data flag
      setHasFbrefData(response.has_fbref_data || false);
    } catch (error: any) {
      console.error('Error loading player details:', error);
      setError(error.response?.data?.mensaje || t('errors.generic'));
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const loadPlayerPerformances = async () => {
    try {
      setLoadingPerformances(true);
      const response = await sportingService.getPlayerPerformances(playerId!);
      setPerformances(response.performances || []);
    } catch (error: any) {
      console.error('Error loading player performances:', error);
      // Don't show error toast for performances as it's not critical
      setPerformances([]);
    } finally {
      setLoadingPerformances(false);
    }
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return t('players.notAvailable');
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleBackNavigation = () => {
    // Check if we came from the dashboard
    const state = location.state as any;
    if (state?.from === '/dashboard') {
      // Navigate back to dashboard - the search state will be restored from the store
      navigate('/dashboard');
    } else {
      // Default navigation to sporting page
      navigate('/sporting');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <XCircle className="w-20 h-20 mx-auto mb-4 text-red-400" />
          <h2 className="text-2xl font-bold mb-2">{t('common.error')}</h2>
          <p className="text-gray-300 mb-6">{error || t('errors.notFound')}</p>
          <button
            onClick={handleBackNavigation}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackNavigation}
                className="text-white hover:text-blue-400 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="text-2xl font-bold text-white">{t('players.detail')}</h1>
            </div>
            <button
              onClick={() => navigate(`/sporting/player-intelligence/${playerId}`)}
              className="flex items-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-2.5 rounded-lg transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <span className="hidden sm:inline">{t('players.titleDashboardExecutive')}</span>
              <span className="sm:hidden">Dashboard</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8">
        {/* Player Header Card - Redesigned with prominent photo */}
        <div className="bg-gradient-to-r from-blue-800/30 to-blue-900/30 backdrop-blur-sm rounded-3xl p-8 mb-8 border border-white/10">
          <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
            {/* Player Photo - More Prominent */}
            <div className="flex-shrink-0">
              <div className="relative w-40 h-40 lg:w-48 lg:h-48 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-white/20">
                {(player.player_image_url || player.url_foto_jugador) ? (
                  <img
                    src={player.player_image_url || player.url_foto_jugador}
                    alt={(() => {
                      const name = player.player_name || player.nombre_transfermarkt || player.nombre_fbref || t('players.player');
                      return name.split('(')[0].trim() || name;
                    })()}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <User className="w-20 h-20 lg:w-24 lg:h-24 text-white" />
                )}
              </div>
            </div>

            {/* Player Info */}
            <div className="flex-1 text-white text-center lg:text-left">
              <div className="mb-4">
                <h2 className="text-4xl lg:text-5xl font-bold mb-3 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  {(() => {
                    const name = player.player_name || player.nombre_transfermarkt || player.nombre_fbref || t('players.notAvailable');
                    // Remove "(id)" format - extract only the name before the opening parenthesis
                    const cleanName = name.split('(')[0].trim();
                    return cleanName || name;
                  })()}
                </h2>
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 text-gray-300">
                  <span className="flex items-center space-x-2 bg-white/10 px-3 py-1.5 rounded-full">
                    <Target className="w-4 h-4" />
                    <span className="font-medium">
                      {translatePosition(player.position || player.posicion_transfermarkt || player.posicion_fbref || t('players.notAvailable'), currentLang)}
                    </span>
                  </span>
                  {(player.current_club_name || player.equipo_transfermarkt || player.equipo_fbref) && (
                    <span className="flex items-center space-x-2 bg-white/10 px-3 py-1.5 rounded-full">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium">{player.current_club_name || player.equipo_transfermarkt || player.equipo_fbref}</span>
                    </span>
                  )}
                  {(player.player_id || player.id_transfermarkt) && (
                    <span className="flex items-center space-x-2 bg-white/10 px-3 py-1.5 rounded-full">
                      <Shield className="w-4 h-4" />
                      <span className="font-medium text-sm">ID: {player.player_id || player.id_transfermarkt}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Quick Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-white">
                    {(() => {
                      // Calculate age from date_of_birth if available
                      const birthDate = player.date_of_birth || player.fecha_nacimiento_transfermarkt || player.fecha_nacimiento_fbref;
                      if (birthDate) {
                        const today = new Date();
                        const birth = new Date(birthDate);
                        let age = today.getFullYear() - birth.getFullYear();
                        const monthDiff = today.getMonth() - birth.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                          age--;
                        }
                        return age;
                      }
                      return player.edad_transfermarkt || player.edad_fbref || 'N/A';
                    })()}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide">{t('players.years')}</div>
                </div>
                {hasFbrefData && (
                  <>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold text-green-400">{player.goles || 0}</div>
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Goles</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold text-blue-400">{player.asistencias || 0}</div>
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Asistencias</div>
                    </div>
                  </>
                )}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-400">{formatMarketValue(player.valor_mercado)}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide">{t('players.valor')}</div>
                </div>
              </div>

              {/* Competition Info */}
              {player.competicion && (
                <div className="mt-4 flex items-center justify-center lg:justify-start space-x-2 text-sm text-gray-400">
                  <Trophy className="w-4 h-4" />
                  <span>{player.competicion}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Información Básica */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>{t('players.basicInfo')}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.age')}</span>
                  <span className="font-medium">
                    {(() => {
                      const birthDate = player.date_of_birth || player.fecha_nacimiento_transfermarkt || player.fecha_nacimiento_fbref;
                      if (birthDate) {
                        const today = new Date();
                        const birth = new Date(birthDate);
                        let age = today.getFullYear() - birth.getFullYear();
                        const monthDiff = today.getMonth() - birth.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                          age--;
                        }
                        return `${age} ${t('players.years')}`;
                      }
                      return `${player.edad_transfermarkt || player.edad_fbref || t('players.notAvailable')} ${t('players.years')}`;
                    })()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.position')}</span>
                  <span className="font-medium">
                    {translatePosition(player.position || player.posicion_transfermarkt || player.posicion_fbref || t('players.notAvailable'), currentLang)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.team')}</span>
                  <span className="font-medium">{player.current_club_name || player.equipo_transfermarkt || player.equipo_fbref || t('players.notAvailable')}</span>
                </div>
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.league')}</span>
                  <span className="font-medium">{player.competicion || t('players.notAvailable')}</span>
                </div>
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.nationality')}</span>
                  <span className="font-medium">{player.citizenship || player.nacion || t('players.notAvailable')}</span>
                </div>
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.marketValue')}</span>
                  <span className="font-medium">{formatMarketValue(player.valor_mercado)}</span>
                </div>
                {(player.height || player.altura) && (
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.height')}</span>
                    <span className="font-medium">{player.height || (player.altura ? `${player.altura} cm` : t('players.notAvailable'))}</span>
                  </div>
                )}
                {(player.foot || player.pie_habil) && (
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.dominantFoot')}</span>
                    <span className="font-medium">
                      {translateFoot(player.foot || player.pie_habil || t('players.notAvailable'), currentLang)}
                    </span>
                  </div>
                )}
                {(player.place_of_birth || player.lugar_nacimiento) && (
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.placeOfBirth')}</span>
                    <span className="font-medium">{player.place_of_birth || player.lugar_nacimiento}</span>
                  </div>
                )}
                {(player.contract_expires || player.fecha_fin_contrato) && (
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.contractEnd')}</span>
                    <span className="font-medium">{formatDate(player.contract_expires || player.fecha_fin_contrato)}</span>
                  </div>
                )}
                {player.contract_there_expires && (
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.loanExpiry')}</span>
                    <span className="font-medium">{formatDate(player.contract_there_expires)}</span>
                  </div>
                )}
                {(player.player_agent_name || player.player_agent) && (
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.agent')}</span>
                    <span className="font-medium">{player.player_agent_name || player.player_agent}</span>
                  </div>
                )}
                {player.social_media_url && (
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.socialMedia')}</span>
                    <a 
                      href={player.social_media_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-1"
                    >
                      <span>{t('players.viewSocialMedia')}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Estadísticas Básicas - Only show if has fbref data */}
            {hasFbrefData && (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                  <Target className="w-5 h-5" />
                  <span>{t('players.basicStats')}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.goals')}</span>
                    <span className="font-medium">{player.goles || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.assists')}</span>
                    <span className="font-medium">{player.asistencias || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Goles Esperados (xG) - Only show if has fbref data */}
            {hasFbrefData && (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                  <Activity className="w-5 h-5" />
                  <span>{t('players.expectedGoals')}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.expectedGoalsLabel')}</span>
                    <span className="font-medium">{player.xg_esperado || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.expectedGoalsNoPenalties')}</span>
                    <span className="font-medium">{player.npxg_esperado || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.expectedAssists')}</span>
                    <span className="font-medium">{player.xag_esperado || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.expectedGAPlusA')}</span>
                    <span className="font-medium">{player.npxg_mas_xag_esperado || t('players.notAvailable')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Progresión del Balón - Only show if has fbref data */}
            {hasFbrefData && (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                  <Zap className="w-5 h-5" />
                  <span>{t('players.ballProgression')}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.withBall')}</span>
                    <span className="font-medium">{player.progresion_conduccion || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.withPasses')}</span>
                    <span className="font-medium">{player.progresion_pase || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.withDribbles')}</span>
                    <span className="font-medium">{player.progresion_regate || t('players.notAvailable')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Rendimiento por Partido (Real) - Only show if has fbref data */}
            {hasFbrefData && (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5" />
                  <span>{t('players.realPerformance')}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.goalsPerMatch')}</span>
                    <span className="font-medium">{player.goles_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.assistsPerMatch')}</span>
                    <span className="font-medium">{player.asistencias_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.gaPlusAPerMatch')}</span>
                    <span className="font-medium">{player.goles_mas_asistencias_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.goalsNoPenaltiesPerMatch')}</span>
                    <span className="font-medium">{player.goles_menos_penales_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.gaPlusANoPenaltiesPerMatch')}</span>
                    <span className="font-medium">{player.goles_mas_asistencias_menos_penales_por_90|| t('players.notAvailable')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Rendimiento por Partido (Esperado) - Only show if has fbref data */}
            {hasFbrefData && (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                  <Heart className="w-5 h-5" />
                  <span>{t('players.expectedPerformance')}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.xgPerMatch')}</span>
                    <span className="font-medium">{player.xg_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.xagPerMatch')}</span>
                    <span className="font-medium">{player.xag_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.xgPlusXagPerMatch')}</span>
                    <span className="font-medium">{player.xg_mas_xag_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.npxgPerMatch')}</span>
                    <span className="font-medium">{player.npxg_por_90 || t('players.notAvailable')}</span>
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-gray-400">{t('players.npxgPlusXagPerMatch')}</span>
                  <span className="font-medium">{player.npxg_mas_xag_por_90 || t('players.notAvailable')}</span>
                </div>
              </div>
            </div>
            )}

            {/* Información Adicional */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                <DollarSign className="w-5 h-5" />
                <span>{t('players.additionalInfo')}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.height')}</span>
                  <span className="font-medium">{player.altura ? `${player.altura} cm` : t('players.notAvailable')}</span>
                </div>
                {hasFbrefData && (
                  <>
                    <div className="flex items-center justify-between text-white">
                      <span className="text-gray-400">{t('players.matchesPlayed')}</span>
                      <span className="font-medium">{player.partidos_jugados || t('players.notAvailable')}</span>
                    </div>
                    <div className="flex items-center justify-between text-white">
                      <span className="text-gray-400">{t('players.minutesPlayed')}</span>
                      <span className="font-medium">{player.minutos_jugados || t('players.notAvailable')}</span>
                    </div>
                    <div className="flex items-center justify-between text-white">
                      <span className="text-gray-400">{t('players.yellowCards')}</span>
                      <span className="font-medium">{player.tarjetas_amarillas || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-white">
                      <span className="text-gray-400">{t('players.redCards')}</span>
                      <span className="font-medium">{player.tarjetas_rojas || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-white">
                      <span className="text-gray-400">{t('players.season')}</span>
                      <span className="font-medium">{player.anio_temporada || t('players.notAvailable')}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between text-white">
                  <span className="text-gray-400">{t('players.extractionDate')}</span>
                  <span className="font-medium">{formatDate(player.fecha_extraccion)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance History Section - Condensed Design */}

        {!loadingPerformances && performances.length > 0 && (
          <div className="mt-8">
            <div className="bg-gradient-to-r from-purple-800/30 to-purple-900/30 backdrop-blur-sm rounded-3xl p-6 mb-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Trophy className="w-6 h-6 text-yellow-400" />
                  <h2 className="text-2xl font-bold text-white">{t('performanceHistoryTitle')}</h2>
                </div>
                <div className="text-sm text-gray-300">
                  {performances.length} {t('totalCompetitions')}
                </div>
              </div>

              {/* Career Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {(() => {
                  const totalMatches = performances.reduce((sum, p) => sum + (p.nb_on_pitch || 0), 0);
                  const totalGoals = performances.reduce((sum, p) => sum + (p.goals || 0) + (p.penalty_goals || 0), 0);
                  const totalAssists = performances.reduce((sum, p) => sum + (p.assists || 0), 0);
                  const totalMinutes = performances.reduce((sum, p) => sum + (p.minutes_played || 0), 0);
                  
                  return (
                    <>
                      <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-green-400 mb-1">{totalGoals}</div>
                        <div className="text-xs text-gray-300 uppercase tracking-wide">{t('goals')}</div>
                      </div>
                      <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-blue-400 mb-1">{totalAssists}</div>
                        <div className="text-xs text-gray-300 uppercase tracking-wide">{t('assists')}</div>
                      </div>
                      <div className="bg-purple-500/20 border border-purple-400/30 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-purple-400 mb-1">{totalMatches}</div>
                        <div className="text-xs text-gray-300 uppercase tracking-wide">{t('matches')}</div>
                      </div>
                      <div className="bg-indigo-500/20 border border-indigo-400/30 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-indigo-400 mb-1">{Math.round(totalMinutes / 90)}</div>
                        <div className="text-xs text-gray-300 uppercase tracking-wide">{t('matches90')}</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Condensed Seasons Table/List */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                      <th className="px-4 py-3">{t('season')}</th>
                      <th className="px-4 py-3">{t('team')}</th>
                      <th className="px-4 py-3 text-center">{t('playedMatches')}</th>
                      <th className="px-4 py-3 text-center">{t('goalsShort')}</th>
                      <th className="px-4 py-3 text-center">{t('assistsShort')}</th>
                      <th className="px-4 py-3 text-center">{t('minutes')}</th>
                      <th className="px-4 py-3 text-center">{t('yellowCards')}</th>
                      <th className="px-4 py-3 text-center">{t('redCards')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {(showAllSeasons ? performances : performances.slice(0, 5)).map((perf, index) => {
                      const totalMatches = (perf.nb_on_pitch || 0);
                      const totalGoals = (perf.goals || 0) + (perf.penalty_goals || 0);
                      const totalCards = (perf.yellow_cards || 0) + (perf.second_yellow_cards || 0);
                      const totalRedCards = (perf.direct_red_cards || 0) + (perf.second_yellow_cards || 0);
                      
                      return (
                        <tr key={`${perf.season_name}-${perf.competition_name}-${index}`} className="hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-white font-semibold">{perf.season_name}</div>
                            {perf.competition_name && (
                              <div className="text-xs text-gray-400 mt-0.5">{perf.competition_name}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-300 text-sm">{perf.team_name || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-white font-bold">{totalMatches}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-green-400 font-bold">{totalGoals}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-blue-400 font-bold">{perf.assists || 0}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-gray-300 text-sm">{perf.minutes_played || 0}'</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-yellow-400 font-semibold">{totalCards}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-red-400 font-semibold">{totalRedCards}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Show More/Less Button */}
              {performances.length > 5 && (
                <div className="border-t border-white/10 p-4 text-center">
                  <button
                    onClick={() => setShowAllSeasons(!showAllSeasons)}
                    className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
                  >
                    <span>
                      {showAllSeasons
                        ? t('seeLess')
                        : `${t('seeAll')} (${performances.length})`}
                    </span>
                    {showAllSeasons ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}


        {/* No Performance Data Message */}
        {!loadingPerformances && performances.length === 0 && (
          <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
            <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-300 text-lg">
              {t('noPerformanceData')}
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default PlayerDetailPage;
